import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { TrendingUp, Download, Users, Euro, AlertCircle, Heart, Bug, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#e11d48', '#3b82f6', '#f59e0b', '#16a34a', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#eab308', '#6366f1'];

function KpiCard({ icon: Icon, label, value, hint, color = 'text-red-500' }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 text-xs text-zinc-400 mb-1"><Icon size={14} className={color} /> {label}</div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function ChartCard({ title, children, onExport }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-zinc-200">{title}</h3>
        {onExport && <button onClick={onExport} className="text-xs text-zinc-400 hover:text-red-400 flex items-center gap-1"><Download size={12} /> CSV</button>}
      </div>
      <div className="h-64">
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { authFetch } = useAdmin();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/admin/analytics/overview?window=30d`);
      setData(await r.json());
    } catch {}
  }, [authFetch]);

  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);

  async function exportMetric(metric) {
    const r = await authFetch(`${API}/admin/analytics/export?metric=${metric}&window=30d`);
    const blob = await r.blob();
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${metric}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  }

  if (!data) return <div className="p-6 text-zinc-400" data-testid="analytics-loading">Učitavam analitiku…</div>;

  const k = data.kpis;

  return (
    <div className="p-6 space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-2xl font-semibold">Analitika</h1>
        <p className="text-xs text-zinc-400 mt-1">Uživo (auto-osvježavanje svakih 60s). Generirano {new Date(data.generated_at).toLocaleTimeString('hr')}.</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Users} label="Aktivne pretplate" value={k.active_subs} />
        <KpiCard icon={Euro} label="MRR (EUR)" value={`${k.mrr_eur} €`} />
        <KpiCard icon={AlertCircle} label="Na odobrenju" value={k.pending_approvals} hint={`${data.classifieds_pending} oglasa + ${data.jobs_pending} posao`} color="text-amber-500" />
        <KpiCard icon={Heart} label="Donacije ovaj mj." value={`${k.donations_this_month_eur} €`} hint={`${data.donations_count_month} donacija`} color="text-pink-500" />
        <KpiCard icon={Bug} label="Otvorene prijave" value={k.bug_reports_open} color="text-orange-500" />
        <KpiCard icon={UserPlus} label="Novi pretplatnici 7d" value={k.new_subscribers_7d} color="text-emerald-500" />
      </div>

      {/* Large charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard title="Objave po danu (30 dana)" onExport={() => exportMetric('articles_published')}>
          <LineChart data={data.articles_published}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <YAxis tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
            <Line type="monotone" dataKey="value" stroke="#e11d48" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>
        <ChartCard title="Pregledi članaka po danu (30 dana)" onExport={() => exportMetric('article_views')}>
          <LineChart data={data.article_views}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <YAxis tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
            <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>
      </div>

      {/* Secondary grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ChartCard title="Top 10 članaka (ukupno)">
          <BarChart layout="vertical" data={data.top_all}>
            <XAxis type="number" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <YAxis type="category" dataKey="title" width={150} tick={{ fill: '#a1a1aa', fontSize: 9 }} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
            <Bar dataKey="views" fill="#e11d48" />
          </BarChart>
        </ChartCard>

        <ChartCard title="Raspodjela po kategorijama">
          <PieChart>
            <Pie data={data.category_distribution} dataKey="count" nameKey="name" outerRadius={90} innerRadius={40}>
              {data.category_distribution.map((e, i) => <Cell key={i} fill={e.color || COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
          </PieChart>
        </ChartCard>

        <ChartCard title="Status pretplata">
          <BarChart data={Object.entries(data.subs_breakdown).map(([k, v]) => ({ status: k, count: v }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="status" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <YAxis tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
            <Bar dataKey="count" fill="#8b5cf6" />
          </BarChart>
        </ChartCard>

        <ChartCard title="Rast newslettera (30d)" onExport={() => exportMetric('newsletter_growth')}>
          <LineChart data={data.newsletter_growth}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <YAxis tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
            <Line type="monotone" dataKey="value" stroke="#14b8a6" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>

        <ChartCard title="Komentari po danu (30d)" onExport={() => exportMetric('comments_per_day')}>
          <LineChart data={data.comments_per_day}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <YAxis tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
            <Line type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>

        <ChartCard title="MRR timeline (90d)" onExport={() => exportMetric('mrr_timeline')}>
          <LineChart data={data.mrr_timeline}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <YAxis tick={{ fill: '#a1a1aa', fontSize: 10 }} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
            <Line type="monotone" dataKey="value" stroke="#e11d48" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>
      </div>
    </div>
  );
}
